import { useState, useEffect, useMemo, AnchorHTMLAttributes } from 'react';
import { applyGanalyticsTracking, urlIsExternal } from 'src/functions/propagateQueryParams';
import { safeNavigationHref } from 'src/functions/safeNavigationHref';
import { windowIsLoaded } from 'src/functions/windowIsLoaded';
import { isUntranslatedPath } from 'src/utils/untranslated-pages';
import { appendExtraParams } from './useExternalNavigation';
import { useExtraQueryParams } from './useExtraQueryParams';
import { useI18nContext } from './useI18nContext';

const useBaseLink = (
  url: string,
  onClick?: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void
): AnchorHTMLAttributes<HTMLAnchorElement> => {
  const { language } = useI18nContext();
  const { extraQueryParams } = useExtraQueryParams();
  const [queryPropagatedUrl, updateHref] = useState(url);

  useEffect(() => {
    windowIsLoaded().then(() => {
      let finalUrl = safeNavigationHref(url);

      if (extraQueryParams) {
        finalUrl = appendExtraParams(finalUrl, extraQueryParams);
      }

      // Check if this is an untranslated page
      const isUntranslatedPage = isUntranslatedPath(finalUrl);

      // Prepend language prefix for internal links (non-English)
      // Skip untranslated pages - they should always use English URLs
      if (
        !urlIsExternal(finalUrl) &&
        language !== 'en' &&
        !finalUrl.startsWith(`/${language}/`) &&
        !isUntranslatedPage
      ) {
        // Handle root path
        if (finalUrl === '/') {
          finalUrl = `/${language}/`;
        }
        // Handle paths that start with /
        else if (finalUrl.startsWith('/')) {
          finalUrl = `/${language}${finalUrl}`;
        }
      }

      updateHref(finalUrl);
    });
  }, [url, language, extraQueryParams]);

  const onClickToScroll = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    onClick?.(event);

    event.preventDefault();
    document.querySelector(url)?.scrollIntoView({
      behavior: 'smooth',
    });
  };

  const handleClickWithTracking = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    onClick?.(event);
    applyGanalyticsTracking(queryPropagatedUrl)(event);
  };

  const isScrollEvent = url.startsWith('#');

  const targetProp = useMemo(() => {
    return urlIsExternal(url) ? { target: '_blank', rel: 'noreferrer' } : {};
  }, [url]);

  return {
    ...targetProp,
    href: queryPropagatedUrl,
    onClick: isScrollEvent ? onClickToScroll : handleClickWithTracking,
  };
};

export default useBaseLink;
